import React from "react";
import {
  GitBranch,
  KeyRound,
  Layers,
  Link2,
  RefreshCcw,
  Shield,
  Users,
  UserCog,
} from "lucide-react";

export type RBACTabKey =
  | "hierarchy"
  | "roles"
  | "permissions"
  | "resources"
  | "roleResources"
  | "mappings"
  | "users";

export interface RBACTabConfig {
  key: RBACTabKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  count?: number;
}

export const buildTabConfig = (counts: Partial<Record<RBACTabKey, number>>): RBACTabConfig[] => [
  {
    key: "hierarchy",
    label: "Access path",
    description: "Role → role-resource → permissions",
    icon: <GitBranch className="h-4 w-4" />,
    count: counts.hierarchy,
  },
  {
    key: "roles",
    label: "Roles",
    description: "Top-level VMS roles",
    icon: <Shield className="h-4 w-4" />,
    count: counts.roles,
  },
  {
    key: "permissions",
    label: "Permissions",
    description: "Action + scope pairs",
    icon: <KeyRound className="h-4 w-4" />,
    count: counts.permissions,
  },
  {
    key: "resources",
    label: "Resources",
    description: "Domain entities",
    icon: <Layers className="h-4 w-4" />,
    count: counts.resources,
  },
  {
    key: "roleResources",
    label: "Role-resources",
    description: "Sub-roles per resource",
    icon: <UserCog className="h-4 w-4" />,
    count: counts.roleResources,
  },
  {
    key: "mappings",
    label: "Mappings",
    description: "Role-resource ↔ permission",
    icon: <Link2 className="h-4 w-4" />,
    count: counts.mappings,
  },
  {
    key: "users",
    label: "Users",
    description: "Users under your tree",
    icon: <Users className="h-4 w-4" />,
    count: counts.users,
  },
];

interface RBACPageHeaderProps {
  loading: boolean;
  onRefresh: () => void;
}

export const RBACPageHeader: React.FC<RBACPageHeaderProps> = ({ loading, onRefresh }) => (
  <header className="border-b border-cardborder bg-white">
    <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">RBAC management</h1>
        <p className="mt-1 text-sm text-gray-500">
          Start from <strong className="font-medium text-gray-700">Access path</strong> to drill
          role → role-resource → permission mappings, or use the other sections for bulk edits.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-cardborder bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
    </div>
  </header>
);

interface RBACTabNavProps {
  active: RBACTabKey;
  tabs: RBACTabConfig[];
  onChange: (tab: RBACTabKey) => void;
}

export const RBACTabNav: React.FC<RBACTabNavProps> = ({ active, tabs, onChange }) => (
  <nav className="flex flex-col gap-1">
    {tabs.map((tab) => {
      const isActive = active === tab.key;
      return (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={`group flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
            isActive
              ? "bg-brand/10 text-brand"
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          <span className="flex items-center gap-3">
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-md ${
                isActive ? "bg-brand text-white" : "bg-white text-gray-500 group-hover:text-gray-700"
              }`}
            >
              {tab.icon}
            </span>
            <span className="flex flex-col">
              <span className="font-medium leading-tight">{tab.label}</span>
              <span className="text-[11px] text-gray-500">{tab.description}</span>
            </span>
          </span>
          {typeof tab.count === "number" && (
            <span
              className={`min-w-[1.5rem] rounded-full px-2 py-0.5 text-center text-[11px] font-semibold ${
                isActive ? "bg-brand text-white" : "bg-gray-200 text-gray-700"
              }`}
            >
              {tab.count}
            </span>
          )}
        </button>
      );
    })}
  </nav>
);
